<template>
  <div :id="trainer.id" class="trainer-card flex-column flex-lg-row">
    <div class="d-flex">
      <nuxt-link
        class="trainer-link trainer-link-img"
        :to="
          localePath({
            name: 'location-trainer-id',
            params: { id: splitUrl(trainer, 'url'), location: city }
          })
        "
        @click.native="handlePageTransition(false)"
      >
        <el-avatar
          shape="square"
          :src="trainer.avatar"
          fit="cover"
          class="trainer-avatar"
        />
      </nuxt-link>
      <div class="trainer-info">
        <nuxt-link
          class="trainer-link"
          :to="
            localePath({
              name: 'location-trainer-id',
              params: { id: splitUrl(trainer, 'url'), location: city }
            })
          "
          @click.native="handlePageTransition(false)"
        >
          <div class="d-flex align-items-center">
            <span class="trainer-name">{{ trainerFullName }}</span>
            <span v-if="trainer.age" class="mob-gray ml-3 trainer-age">
              {{ trainer.age }}
            </span>
          </div>
          <div class="mt-2">
            <p
              v-for="(sport, idx) in trainer.sports"
              :key="idx"
              class="sport-line"
            >
              <span class="icon-wrapper"
                ><img class="sport-icon" :src="getSportIcon(sport.id)"
              /></span>
              <span class="fw6 ml-1 ml-lg-0">{{ sport.value }}</span>
              <span class="mob-gray">&nbsp; - {{ sport.experience }}</span>
            </p>
          </div>
        </nuxt-link>
        <div class="mt-2">
          <p v-for="club in trainer.clubs" :key="'club-' + club.id">
            <span class="icon-wrapper"
              ><i class="icon-location club-sign"
            /></span>
            <nuxt-link
              tag="a"
              class="club-link"
              :to="
                club.address
                  ? localePath({
                      name: 'location-club-id',
                      params: { id: splitUrl(club, 'fullUrl'), location: city }
                    })
                  : ''
              "
              @click.native="handlePageTransition(club)"
            >
              {{ club.title }}
            </nuxt-link>
            <span class="mob-gray">{{ addressFormat(club.address) }}</span>
          </p>
        </div>
      </div>
    </div>
    <div
      class="trainer-options flex-lg-column"
      :class="{ hide: !isNativeDomain }"
    >
      <div class="d-flex price-wrapper">
        <div>
          <span class="mob-size">
            {{ trainerPrice }}
          </span>
          <br />
          <span class="d-none d-lg-block fz-12 fw3 mt-2">{{
            $t('common.for_training')
          }}</span>
        </div>
        <el-checkbox v-model="checked" class="bookmark">
          <i
            :class="checked ? 'icon-star' : 'icon-star-empty'"
            @click="handleBookmarkClick"
          />
        </el-checkbox>
      </div>
      <div class="d-flex rate-wrapper">
        <span class="rate rate-positive">
          <i class="icon-rating-up" />
          {{ positive }}
        </span>
        <span class="rate rate-negative">
          <i class="icon-rating-down" />
          {{ negative }}
        </span>
      </div>
      <MessageButton
        v-if="isNativeDomain"
        class="message-button"
        :trainer-name="trainerFullName"
        :trainer-id="trainer.id"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CheckNativeDomainMixin from '@/dependencies/mixins/check-native-domain'
import MessageButton from '~/components/Search/MessageButton'

export default {
  name: 'TrainerCard',
  components: {
    MessageButton
  },
  mixins: [CheckNativeDomainMixin],
  props: {
    trainer: {
      type: Object,
      default: () => ({})
    }
  },
  data: () => ({
    checked: false,
    dialog: false
  }),
  computed: {
    ...mapGetters({
      bookmarks: 'search/getBookmarksList',
      currencySign: 'user/getCurrencySign',
      city: 'location/getUrlKey',
      isBookmarksShown: 'search/isBookmarksShown'
    }),
    positive() {
      return this.trainer?.rating?.positive
    },
    negative() {
      return this.trainer?.rating?.negative
    },
    trainerFullName() {
      return `${this.trainer.first_name} ${this.trainer.last_name}`
    },
    trainerPrice() {
      return `${this.$t('common.from')} ${this.currencySign}${
        this.trainer.price
      }`
    }
  },
  watch: {
    checked() {
      if (this.checked) {
        this.addBookmark(this.trainer)
      } else {
        this.deleteBookmark(this.trainer.id)
      }
    }
  },
  beforeMount() {
    this.checked = !!this.bookmarks[this.trainer.id]
  },
  methods: {
    ...mapActions({
      addBookmark: 'search/ADD_BOOKMARK',
      deleteBookmark: 'search/DELETE_BOOKMARK',
      setFocusedTrainer: 'search/FOCUS_TRAINER'
    }),

    getSportIcon(id) {
      try {
        return require(`~/assets/img/sports/small/${id}.png`)
      } catch {
        return require(`~/assets/img/sports/small/null.png`)
      }
    },

    splitUrl(_obj, _key) {
      const splitUrl = _obj?.[_key].split('/')
      return splitUrl[splitUrl.length - 1]
    },

    handleBookmarkClick() {
      this.checked
        ? this.$bus.$emit('success', 'bookmark-deleted')
        : this.$bus.$emit('success', 'bookmark-saved')
    },

    handlePageTransition(_club) {
      if (!_club.address && _club) {
        this.$bus.$emit('warning', 'club-not-moderated')
      }
      this.setFocusedTrainer(this.trainer.id)
    },

    addressFormat(_address) {
      return `${_address ? `(${_address})` : ''}`
    }
  }
}
</script>

<style scoped lang="scss">
@import '~assets/css/variables.scss';
.trainer-card {
  display: flex;
  justify-content: space-between;
  padding: 12px 12px 0 12px;
  background-color: white;
  border-bottom: 1px solid $border-gray;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
}
.trainer-avatar {
  max-width: 65px;
  min-width: 65px;
  height: 65px;
  min-height: 65px;
  border-radius: 0 !important;
}

.trainer-link {
  &:hover,
  &:hover ~ .trainer-info {
    .trainer-name {
      text-decoration: underline;
      color: #333;
    }
  }
}

.trainer-link-img {
  max-height: 206px;
}

.trainer-name {
  color: black;
  font-size: 16px;
  font-weight: 500;
  text-decoration: underline;
}
.trainer-age {
  font-size: 14px;
}

.club-link {
  color: $primary;
  text-decoration: underline;
  &:hover {
    color: $primary;
  }
}
.sport-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  span {
    font-size: 14px;
    font-family: 'UbuntuR', sans-serif;
    color: black;
  }
}
.sport-icon {
  width: 20px;
  height: 20px;
}
.club-sign {
  color: $primary;
}
.icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
}
.mob-gray {
  color: $gray !important;
}
.mob-size {
  max-width: max-content;
  font-weight: 600;
  font-size: 16px;
  color: black;
  &:hover {
    color: #333;
  }
}
.trainer-options {
  display: flex;
  justify-content: space-between;
}
.rate {
  max-width: max-content;
  font-weight: bold;
}
.rate-positive {
  margin-right: 10px;
  color: #2f8e97;
}
.rate-negative {
  color: #ff5722;
}

@media (min-width: 576px) and (max-width: 991px) {
  .trainer-avatar {
    max-width: 120px;
    min-width: 120px;
    height: 120px;
    min-height: 120px;
  }
}

@media (max-width: 991px) {
  .trainer-avatar {
    margin-right: 15px;
  }
  .accent-button {
    width: 100px;
  }
  .trainer-options {
    align-items: center;
    padding: 10px 0;
    margin-top: 20px;
    border-top: 1px solid #bdbdbd;
  }

  .rate-wrapper {
    min-width: 30%;
    order: 0;
  }
  .price-wrapper {
    order: 1;
    flex-direction: row-reverse;
    justify-content: flex-end;
    align-items: center;
    width: 150px;
  }
  .message-button {
    order: 2;
  }
  .bookmark {
    margin-right: 15px;
    padding: 5px 15px;
    border-right: 1px solid $border-gray;
    border-left: 1px solid $border-gray;
    font-size: 18px;
    .icon-star-empty,
    .icon-star {
      color: #fcb602 !important;
    }
  }
  .club-icon {
    max-width: 15px;
    margin-right: 5px;
    color: $primary;
  }
}
@media (min-width: 992px) {
  .trainer-card {
    padding: 0;
    margin-bottom: 15px;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  }
  .trainer-avatar {
    width: 206px;
    min-width: 206px;
    height: 206px;
    border-radius: 0 !important;
  }
  .trainer-name {
    font-size: 20px;
    text-decoration: none;
  }
  .mob-gray {
    color: black !important;
  }
  .mob-size {
    font-size: 20px;
    font-weight: 500;
  }
  .icon-wrapper {
    display: none;
  }
  .trainer-info {
    padding: 25px 10px 15px 30px;
  }
  .trainer-options {
    margin-top: 15px;
    margin-bottom: 20px;
    padding: 0 15px;
    border-left: 1px solid $border-gray;
    max-width: 190px;
    min-width: 190px;

    &.hide {
      justify-content: normal;

      .price-wrapper {
        margin-bottom: 22px;
      }
    }
  }
  .price-wrapper {
    justify-content: space-between;
  }
  .bookmark {
    display: flex;
    justify-content: flex-end;
    .icon-star-empty,
    .icon-star {
      font-size: 24px;
    }
    .icon-star-empty {
      color: $border-gray !important;
      font-weight: lighter !important;
    }
    .icon-star {
      color: #fcb602 !important;
      font-weight: lighter !important;
    }
  }
}
</style>
